import React, { useState, useEffect, useMemo } from 'react';
import Loading from '../../Common/Loading';

// --- IMPORTS FOR IMAGES ---
// Adjust the path '../../Images/' to match where your Images folder actually is relative to this file.
import go_n from './Images/go_n.gif';
import go_o from './Images/go_o.gif';
import go_d from './Images/go_d.gif';
import close_n from './Images/close_n.gif';
import close_o from './Images/close_o.gif';
import close_d from './Images/close_d.gif';

// --- IMAGE MAPPING ---
// We create a lookup object so we can still use dynamic keys like "go_n"
const btnImages: Record<string, string> = {
    'go_n': go_n,
    'go_o': go_o,
    'go_d': go_d,
    'close_n': close_n,
    'close_o': close_o,
    'close_d': close_d
};

// --- Interfaces ---
interface CallRecord {
    tutor: string;
    outgoing_total: number;
    outgoing_answered_hash: number;
    outgoing_answered_perc: number;
    outgoing_na_hash: number;
    outgoing_na_perc: number;
    inc_total: number;
    inc_direct: number;
    inc_transferred: number;
    other: number;
    total_supports: number;
    total_conversations: number;
}

type SortKey = keyof CallRecord;

// --- Helper: Legacy Image Button Component ---
const ImageButton: React.FC<{
    name: string;
    onClick?: () => void;
    style?: React.CSSProperties
}> = ({ name, onClick, style }) => {
    const [suffix, setSuffix] = useState('n'); // n=Normal, o=Over, d=Down

    // Construct the key (e.g., "go_n") and look it up in the import map
    const imageKey = `${name}_${suffix}`;
    const imageSrc = btnImages[imageKey];

    return (
        <img
            src={imageSrc}
            alt={name}
            style={{ cursor: 'pointer', verticalAlign: 'middle', ...style }}
            onMouseEnter={() => setSuffix('o')}
            onMouseLeave={() => setSuffix('n')}
            onMouseDown={() => setSuffix('d')}
            onMouseUp={() => setSuffix('o')}
            onClick={onClick}
            draggable={false}
        />
    );
};

const CallAndSupport: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    // --- Date Init (Today & Today - 7) ---
    const getFormattedDate = (date: Date) => {
        return date.toISOString().split('T')[0];
    };

    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);

    // --- State ---
    const [fromDate, setFromDate] = useState(getFormattedDate(lastWeek));
    const [toDate, setToDate] = useState(getFormattedDate(today));
    const [country, setCountry] = useState('---ALL---');
    const [countries, setCountries] = useState<string[]>([]);

    const [data, setData] = useState<CallRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);
    const [initialLoad, setInitialLoad] = useState(true);
    // --- Effects ---
    useEffect(() => {
        document.body.style.backgroundColor = '#ffffff';

        // Fetch Countries
        const fetchCountries = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/get_dlr_countries`);
                const json = await res.json();
                if (json.countries) setCountries(json.countries);
            } catch (err) { console.error(err); }
        };
        fetchCountries();

        // Auto-load on page load
        handleGo();
    }, []);

    // --- Handlers ---
    const handleGo = async () => {
        setLoading(true);
        if (!initialLoad) {
            setHasSearched(true);
        }
        try {
            const res = await fetch(`${API_BASE_URL}api/call_support_report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    country,
                    from_date: fromDate,
                    to_date: toDate
                })
            });
            const json = await res.json();
            if (json.error) alert(json.error);
            else setData(json.results || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    };

    const handleClose = () => {
        window.location.href = "/SysAdmin";
    };

    const handleSort = (key: SortKey) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        let sortableItems = [...data];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    // Calculate Footer Totals
    const totals = useMemo(() => {
        return data.reduce((acc, row) => {
            acc.outgoing_total += row.outgoing_total;
            acc.outgoing_answered_hash += row.outgoing_answered_hash;
            acc.outgoing_na_hash += row.outgoing_na_hash;
            acc.inc_total += row.inc_total;
            acc.inc_direct += row.inc_direct;
            acc.inc_transferred += row.inc_transferred;
            acc.other += row.other;
            acc.total_supports += row.total_supports;
            acc.total_conversations += row.total_conversations;
            return acc;
        }, {
            outgoing_total: 0, outgoing_answered_hash: 0, outgoing_na_hash: 0,
            inc_total: 0, inc_direct: 0, inc_transferred: 0, other: 0,
            total_supports: 0, total_conversations: 0
        });
    }, [data]);

    const totalOutAnsPerc = totals.outgoing_total > 0 ? Math.round((totals.outgoing_answered_hash / totals.outgoing_total) * 100) : 0;
    const totalOutNaPerc = totals.outgoing_total > 0 ? Math.round((totals.outgoing_na_hash / totals.outgoing_total) * 100) : 0;

    // --- Styles ---
    const styles = {
        body: { fontFamily: 'Arial, Helvetica, sans-serif', textAlign: 'center' as const, backgroundColor: '#fff', minHeight: '100vh', padding: '10px' },
        h1: { fontSize: '1em', fontFamily: 'Arial, Helvetica, sans-serif', textAlign: 'center' as const, fontWeight: 'bold', margin: '10px 0' },
        calContainer: { padding: '5px 0px 5px 50px', width: 'auto', textAlign: 'center' as const, marginBottom: '10px' },
        input: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', border: '1px solid #000000', width: '120px', marginRight: '5px' },
        select: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', border: '1px solid #000000', width: '130px', marginLeft: '10px' },
        btnPlaceholder: { padding: '10px 0px 0px 60px', textAlign: 'center' as const, marginBottom: '15px' },
        gridWrapper: { overflowX: 'auto' as const, overflowY: 'auto' as const, maxHeight: '850px', width: '100%', border: '0px solid black' },
        table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px', fontFamily: 'Arial' },
        th: { backgroundColor: '#328da8', color: 'white', padding: '8px', border: '1px solid black', cursor: 'pointer', whiteSpace: 'nowrap' as const, fontFamily: 'Times New Roman', fontSize: '12pt', textDecoration: 'underline' as const },
        td: { padding: '4px 5px', border: '1px solid #000000', color: 'black', textAlign: 'center' as const, fontFamily: 'Times New Roman', fontSize: '12pt', },
        noRecords: { width: '600px', textAlign: 'left' as const, color: 'Black', fontSize: '20pt', margin: '20px auto' }
    };

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <h1 style={styles.h1}>Call and Support Details</h1>

            <div style={styles.calContainer}>
                <input type="date" style={styles.input} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <span style={{ fontSize: '13px', fontFamily: 'Arial' }}>To: </span>
                <input type="date" style={styles.input} value={toDate} onChange={(e) => setToDate(e.target.value)} />

                <select style={styles.select} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="---ALL---">---ALL---</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div style={styles.btnPlaceholder}>
                {/* Images passed as keys to the lookup object */}
                <ImageButton name="go" onClick={handleGo} style={{ marginRight: '10px' }} />
                <ImageButton name="close" onClick={handleClose} />
            </div>

            {!loading && !initialLoad && hasSearched && data.length === 0 && (
                <div style={styles.noRecords}>No Records Found</div>
            )}

            {data.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div className="col-lg-8" style={{ width: '90%' }}>
                        <div style={styles.gridWrapper}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Sl No</th>
                                        <th style={styles.th} onClick={() => handleSort('tutor')}>Tutor</th>
                                        <th style={styles.th} onClick={() => handleSort('outgoing_total')}>Outgoing Total</th>
                                        <th style={styles.th} onClick={() => handleSort('outgoing_answered_hash')}>Outgoing Answered #</th>
                                        <th style={styles.th} onClick={() => handleSort('outgoing_answered_perc')}>Outgoing Answered %</th>
                                        <th style={styles.th} onClick={() => handleSort('outgoing_na_hash')}>Outgoing N.A #</th>
                                        <th style={styles.th} onClick={() => handleSort('outgoing_na_perc')}>Outgoing N.A %</th>
                                        <th style={styles.th} onClick={() => handleSort('inc_total')}>Inc. Total</th>
                                        <th style={styles.th} onClick={() => handleSort('inc_direct')}>Inc. Direct</th>
                                        <th style={styles.th} onClick={() => handleSort('inc_transferred')}>Inc. Transferred</th>
                                        <th style={styles.th} onClick={() => handleSort('other')}>Other</th>
                                        <th style={styles.th} onClick={() => handleSort('total_supports')}>Total Supports</th>
                                        <th style={styles.th} onClick={() => handleSort('total_conversations')}>Total Conversations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.map((row, idx) => (
                                        <tr
                                            key={idx}
                                            style={{
                                                backgroundColor: idx % 2 === 0 ? '#F4FAFF' : '#FFFFFF'
                                            }}
                                        >
                                            <td style={styles.td}>{idx + 1}</td>
                                            <td style={{ ...styles.td, textAlign: 'left' }}>{row.tutor}</td>
                                            <td style={styles.td}>{row.outgoing_total}</td>
                                            <td style={styles.td}>{row.outgoing_answered_hash}</td>
                                            <td style={styles.td}>{row.outgoing_answered_perc}</td>
                                            <td style={styles.td}>{row.outgoing_na_hash}</td>
                                            <td style={styles.td}>{row.outgoing_na_perc}</td>
                                            <td style={styles.td}>{row.inc_total}</td>
                                            <td style={styles.td}>{row.inc_direct}</td>
                                            <td style={styles.td}>{row.inc_transferred}</td>
                                            <td style={styles.td}>{row.other}</td>
                                            <td style={styles.td}>{row.total_supports}</td>
                                            <td style={styles.td}>{row.total_conversations}</td>
                                        </tr>
                                    ))}

                                    {/* Footer Row */}
                                    <tr style={{ backgroundColor: '#E5F2FF', fontWeight: 'bold' }}>
                                        <td style={styles.td}></td>
                                        <td style={{ ...styles.td, textAlign: 'left' }}>Total</td>
                                        <td style={styles.td}>{totals.outgoing_total}</td>
                                        <td style={styles.td}>{totals.outgoing_answered_hash}</td>
                                        <td style={styles.td}>{totalOutAnsPerc}</td>
                                        <td style={styles.td}>{totals.outgoing_na_hash}</td>
                                        <td style={styles.td}>{totalOutNaPerc}</td>
                                        <td style={styles.td}>{totals.inc_total}</td>
                                        <td style={styles.td}>{totals.inc_direct}</td>
                                        <td style={styles.td}>{totals.inc_transferred}</td>
                                        <td style={styles.td}>{totals.other}</td>
                                        <td style={styles.td}>{totals.total_supports}</td>
                                        <td style={styles.td}>{totals.total_conversations}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallAndSupport;